import clsx from 'clsx';
import { FC } from 'react';

import { useLkBasePath } from '../../../hooks/use-lk-base-path';
import { ButtonLink } from '../common/ButtonLink';
import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Link } from '../common/Link';
import { Text } from '../common/Text';
import {
  WhySgTimeToMarketEfficiencyIcon,
  WhySgTimeToMarketSpeedIcon,
  WhySgTimeToMarketTimelineCheckIcon,
  WhySgTimeToMarketTrendZigzagIcon,
} from '../whySg/icons/WhySgTimeToMarketIcons';

import styles from './WhySgTimeToMarketBlock.module.scss';

const TIMELINE_STEPS: Array<{ title: string; range: string; final?: boolean }> = [
  { title: 'Integration Setup', range: 'Day 1–3' },
  { title: 'SDK Configuration', range: 'Day 4–7' },
  { title: 'Testing & QA', range: 'Day 8–12' },
  { title: 'Go Live!', range: 'Day 14', final: true },
];

export const WhySgTimeToMarketBlock: FC = () => {
  const lkBasePath = useLkBasePath();

  return (
    <Container className={styles.root}>
      <div className={styles.layout}>
        <div className={styles.contentRow}>
          <div className={styles.leftColumn}>
            <Flex direction={'column'} gap={4} className={styles.intro}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionLabelLine} aria-hidden />

                <Text type={'medium14'} color={'green'} className={styles.sectionEyebrow}>
                  Time-to-market
                </Text>
              </div>

              <Text type={'regular48'} color={'white'} className={styles.title}>
                Rapid Time-to-Market
              </Text>

              <Text type={'regular16'} color={'white50'} whiteSpace={'normal'} className={styles.description}>
                Launch your payment platform faster than ever. Our turnkey infrastructure and pre-built components
                eliminate months of development, letting you focus on what matters — your business.
              </Text>
            </Flex>

            <div className={styles.featureCards}>
              <div className={styles.featureRow}>
                <Flex className={styles.iconWrap} alignItems={'center'} justifyContent={'center'} aria-hidden>
                  <WhySgTimeToMarketSpeedIcon />
                </Flex>

                <div className={styles.featureBody}>
                  <Text type={'medium16'} color={'white'}>
                    Speed
                  </Text>

                  <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
                    Go live in as little as 2 weeks
                  </Text>
                </div>
              </div>

              <div className={styles.featureRow}>
                <Flex className={styles.iconWrap} alignItems={'center'} justifyContent={'center'} aria-hidden>
                  <WhySgTimeToMarketEfficiencyIcon />
                </Flex>

                <div className={styles.featureBody}>
                  <Text type={'medium16'} color={'white'}>
                    Efficiency
                  </Text>

                  <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
                    Use our comprehensive SDKs and pre-built modules to launch your platform instantly
                  </Text>
                </div>
              </div>
            </div>

            <Flex className={styles.actions} wrap={'wrap'} alignItems={'center'}>
              <ButtonLink className={styles.ctaFilled} href={`${lkBasePath}/registration`} isOutsideLink>
                Get Started Now
              </ButtonLink>

              <Link href={'/docs'} className={styles.docsLink}>
                View Documentation
              </Link>
            </Flex>
          </div>

          <aside className={styles.timelineCard}>
            <span className={styles.badgeWeeks}>2 Weeks</span>

            <div className={styles.timelineCardInner}>
              <header className={styles.timelineHeader}>
                <Text type={'medium14'} color={'green'} className={styles.timelineEyebrow}>
                  Launch timeline
                </Text>

                <div className={styles.timelineHeaderAside}>
                  <span className={styles.timelineTrendIcon} aria-hidden>
                    <WhySgTimeToMarketTrendZigzagIcon />
                  </span>
                </div>
              </header>

              <ul className={styles.timelineList}>
                {TIMELINE_STEPS.map((step, index) => (
                  <li key={step.title} className={styles.timelineItem}>
                    {step.final ? (
                      <div className={clsx(styles.timelineMarker, styles.timelineMarkerDone)}>
                        <WhySgTimeToMarketTimelineCheckIcon />
                      </div>
                    ) : (
                      <div className={styles.timelineMarker}>{index + 1}</div>
                    )}

                    <Text type={'medium14'} color={step.final ? 'green' : 'white'} className={styles.timelineStepTitle}>
                      {step.title}
                    </Text>

                    <Text type={'regular12'} color={'white50'} className={styles.timelineRange}>
                      {step.range}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </Container>
  );
};
